import { FC, useEffect, useState } from "react";
import { Controller } from "react-hook-form";
import Select, { GroupBase, MultiValue, OptionsOrGroups } from "react-select";
import { useDebouncedCallback } from "use-debounce";

import HubInputField from "../inputType";
import useFetch from "src/hooks/useFetch";

import { APIEndpoints } from "src/types/apiTypes";
import { DiagnosisResponseProps, CaseStepOneProps, ProcedureRequestProps } from "./types";
import { SITE_OF_CARE_DROPDOWN } from "src/constants/contact";
import {format, parse} from "date-fns";
import DatePicker from "react-datepicker";
import ReactInputMask from "react-input-mask";


const CaseStepOne: FC<CaseStepOneProps> = ({ control, isVisible, watch, setValue, getValues }): JSX.Element => {
  const [openDatePicker, changeDatePickerVisibility] = useState<boolean>(false)  

  const diagnosisCode = watch("prescription_attributes.diagnosis_code");


  /**
   * @GetProcedureListing API to call getProcedureListing
   */
  const { data: procedureListing } = useFetch<ProcedureRequestProps>(APIEndpoints.GET_PROCEDURE_DROPDOWN);


  /**
   * @SearchDiagnosisBySearchValue search by API for diagnosis
   * @first initialize useFetch() with diagnosis
   * @second search diagnosis by search term
   */
  const { loading: diagnosisLoader, data: rfcListing, mutate: getDiagnosis, removeErrorAndData: clearDiagnosisData } =
    useFetch<DiagnosisResponseProps>(APIEndpoints.GET_DIAGNOSIS_DROPDOWN);

  /** @second */
  const searchDiagnosis = (search: string) => {
    if (typeof search === "string" && search.length >= 2) {
      clearDiagnosisData();
      getDiagnosis(null, null, { search });
    }
  };

  const debounced = useDebouncedCallback(searchDiagnosis, 1500);

  useEffect(() => {
    if (diagnosisCode || (rfcListing && rfcListing.data)) {
      setTimeout(() => clearDiagnosisData(), 150);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [diagnosisCode]);



  return (
    <div className={`card mb-3 ${!isVisible ? 'd-none' : ''}`}>
      <div className="card-header">
        <h4 className="m-0">Getting Started</h4>
      </div>

      <div className="card-body">
        <p>
          In order to process your request for benefit investigation, the following
          information must be completed. All information is kept confidential according
          to HIPAA guidelines. Please fill out the fields below as completely as possible.
          Missing fields may result in a delay to processing patient benefits in a timely manner.
        </p>

        <div className="row">
          <div className="col-lg-6" onFocus={() => changeDatePickerVisibility(false)} tabIndex={-1}>
            <Controller
              name="procedure_attributes.0.cpt_code"
              control={control}
              defaultValue=""
              render={({ field, fieldState: { error } }) => (
                <HubInputField
                  inputId="procedure_attributes.0.cpt_code"
                  inputLabel="Procedure (CPT)"
                  isRequired={true}
                  isRowBased={true}
                  isRowInsideAnother={true}
                  field={field}
                  error={error}
                  isDropdown={true}
                  isSearchable={false}
                  inputPlaceholder="Select a Procedure ..."
                  dropdownData={
                    (procedureListing && procedureListing.data && procedureListing.data.length > 0 ?
                      procedureListing.data.map(
                        (procedure) => ({
                          value: procedure.code,
                          label: `${procedure.code} - ${procedure.description}`
                        })
                      ) : []
                    ) as unknown as OptionsOrGroups<string, GroupBase<string>>
                  }
                />
              )}
            />
          </div>
        </div>

        <div className="row">
          <div className="col-lg-6">
            <Controller
              name="prescription_attributes.qty"
              control={control}
              defaultValue={1}
              render={({ field, fieldState: { error } }) => (
                <div className="row mb-3">
                  <label
                    htmlFor="prescription_attributes.qty"
                    className="form-label col-lg-4 col-md-4 col-12"
                  >
                    # of Units *
                  </label>
                  <div className="col-lg-8 col-md-8 col-12">
                    <div className="input-group">
                    <input
                      type="text" id="prescription_attributes.qty"
                      style={{ zIndex: 0 }}
                      className="form-control"
                      {...field}
                    />
                      <span className="input-group-text fw-bold" id="basic-addon2">Units</span>
                    </div>
                    {error && error.message ? (
                      <div dangerouslySetInnerHTML={{ __html: error.message }} className="text-danger text-small mt-2"></div>
                    ) : null}
                  </div>
                </div>
              )}
            />
          </div>
          <div className="col-lg-6">
            <Controller
              name="prescription_attributes.note" control={control} defaultValue=""
              render={({ field, fieldState: { error } }) => (
                <HubInputField
                  inputId="prescription_attributes.note"
                  isRowBased={true}
                  isRowInsideAnother={true}
                  isRequired={false}
                  field={field}
                  inputLabel="Notes"
                  inputPlaceholder="Note Shared With Patient"
                  error={error}
                />
              )}
            />
          </div>
        </div>

        <div className="row">
          <div className="col-lg-6">
            <div className="row mb-3">
              <label htmlFor="prescription_attributes.diagnosis_code" className="form-label col-lg-4 col-md-4 col-12">
                Diagnosis Code *
              </label>
              <div className="col-lg-8 col-md-8 col-12" onFocus={() => changeDatePickerVisibility(false)} tabIndex={-1}>
                <Controller
                  name="prescription_attributes.diagnosis_code"
                  control={control}
                  defaultValue={[]}
                  render={({ field, fieldState: { error } }) => (
                    <>
                      <Select
                        options={
                          (rfcListing && rfcListing.data && rfcListing.data.length > 0 ?
                            rfcListing.data.map((option) => ({ value: option.fullname, label: option.fullname }))
                            : []) as unknown as OptionsOrGroups<string, GroupBase<string>>
                        }
                        id="prescription_attributes.diagnosis_code"
                        isSearchable={true}
                        isMulti={true}
                        placeholder="Enter Diagnosis Code"
                        {...field}
                        noOptionsMessage={() => diagnosisLoader ? "Loading..." : "Please enter 2 or more characters"}
                        value={
                          field.value.map((item) => ({
                            value: item,
                            label: item,
                          })) as unknown as MultiValue<unknown>
                        }
                        onChange={
                          (val) => {
                            field.onChange(val.map((item: unknown) => (item as { value: string; }).value))
                          }
                        }
                        onInputChange={debounced}
                      />

                      {error && error.message ? (
                        <div className="text-danger text-small mt-2">{error.message}</div>
                      ) : null}
                    </>
                  )}
                />
              </div>
            </div>
          </div>
          <div className="col-lg-6">
            <Controller
              name="prescription_attributes.direction"
              control={control}
              defaultValue=""
              render={({ field, fieldState: { error } }) => (
                <HubInputField
                  inputId="prescription_attributes.direction"
                  isRowBased={true}
                  isRequired={false}
                  isRowInsideAnother={true}
                  field={field}
                  inputLabel="Services Requested"
                  inputPlaceholder=""
                  error={error}
                />
              )}
            />
          </div>
        </div>

        <div className="row">
          <div className="col-lg-6">
            <Controller
              name="prescription_attributes.scheduled_date_of_service"
              control={control}
              defaultValue=""
              render={({ field, fieldState: { error } }) => (
                <div className="row mb-3">
                  <label htmlFor="prescription_attributes.scheduled_date_of_service" className="form-label col-lg-4 col-md-4 col-12">
                    Schedule Date of Service
                  </label>
                  <div className="col-lg-8 col-md-8 col-12">
                    <DatePicker
                      selected={
                        field.value
                          ? parse(field.value, "yyyy-MM-dd", new Date()) // Parse default yyyy-MM-dd to a Date
                          : null
                      }
                      onChange={(date) => {
                        field.onChange(date ? format(date, "yyyy-MM-dd") : ""); // Convert back to yyyy-MM-dd for consistency
                      }}
                      open={openDatePicker}
                      onClickOutside={() => 
                          changeDatePickerVisibility(false)
                      }
                      onFocus={() => changeDatePickerVisibility(true)}
                      id="prescription_attributes.scheduled_date_of_service"
                      dateFormat="MM/dd/yyyy" // User-facing format
                      className={`form-control ${error?.message ? "is-invalid" : ""}`}
                      placeholderText="mm/dd/yyyy"
                      showMonthDropdown
                      showYearDropdown
                      onBlur={(e) => {
                        if (!e.currentTarget.contains(document.activeElement))
                          changeDatePickerVisibility(false)
                      }}
                      customInput={
                        <ReactInputMask mask="99/99/9999">
                          {(inputProps: {}) => <input {...inputProps} />}
                        </ReactInputMask>
                      }
                      minDate={new Date()}
                      wrapperClassName="w-100"  
                    />
                    {error && error.message ? (
                      <div dangerouslySetInnerHTML={{__html: error.message}} className="text-danger text-small mt-2 "></div>
                    ) : null}
                  </div>
                </div>
              )}
            />
          </div>
          <div className="col-lg-6" onFocus={() => changeDatePickerVisibility(false)} tabIndex={-1}>
            <Controller
              name="case_attributes.ship_to"
              control={control}
              defaultValue={SITE_OF_CARE_DROPDOWN[1].value}
              render={({ field, fieldState: { error } }) => (
                <HubInputField
                  isRequired={true}
                  isRowBased={true}
                  isRowInsideAnother={true}
                  inputId="case_attributes.ship_to"
                  inputLabel="Care Site"
                  inputPlaceholder="Select Site of Care..."
                  field={field}
                  error={error}
                  isDropdown={true}
                  isSearchable={false}
                  dropdownData={
                    SITE_OF_CARE_DROPDOWN.map(
                      (siteofCare) => ({ value: siteofCare.value, label: `${siteofCare.name}` })
                    ) as unknown as OptionsOrGroups<string, GroupBase<string>>
                  }
                />
              )}
            />
          </div>
        </div>

         <Controller
          name="prescription_attributes.written_date"
          control={control}
          defaultValue={format(new Date(), 'MM/dd/yyyy')}
          render={() => <></>}
         />

        <Controller
          name="prescription_attributes.drug_name"
          control={control}
          defaultValue="StimRouter Neuromodulation System"
          render={() => <></>}
        />
      </div>
    </div>
  );
};

export default CaseStepOne;
